import { FunnelStep, Image, StepOption } from '../types/cards';
import {getOrCreateAttributionUserId} from "../utils/attribution";
import getCurrentI18nLocale from "../utils/getCurrentI18nLocale";

interface Locale {
    name: string;
    code: string;
    isDefault: boolean;
}

export interface SupportEmailData {
    email: string;
    name: string;
    topic: string;
    description: string;
}


const SERVER_URL = process.env.REACT_APP_API_URL || 'http://localhost:1337';
const LOCALES = process.env.REACT_APP_ENABLED_LOCALES || 'us,ja'


const transformImageUrl = (url: string | null): string | null => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${SERVER_URL}${url}`;
};

const transformImageFormats = (image: Image | null): Image | null => {
    if (!image) return null;

    return {
        ...image,
        url: transformImageUrl(image.url) || '',
        formats: {
            ...image.formats,
            thumbnail: image.formats.thumbnail ? {
                ...image.formats.thumbnail,
                url: transformImageUrl(image.formats.thumbnail.url) || ''
            } : null,
            small: image.formats.small ? {
                ...image.formats.small,
                url: transformImageUrl(image.formats.small.url) || ''
            } : null
        }
    };
};

const transformStepOptions = (options: StepOption[]): StepOption[] => {
    if (!Array.isArray(options)) return [];
    return options.map(option => ({
        ...option,
        image: transformImageFormats(option.image)
    }));
};

const transformStep = (step: any): FunnelStep | null => {
    if (!step) return null;

    const transformedStep = {
        ...step,
        stepContent: Array.isArray(step.stepContent) ? step.stepContent.map((content: any) => ({
            ...content,
            options: content.options ? transformStepOptions(content.options) : [],
            mainBanner: content.mainBanner ? transformImageFormats(content.mainBanner) : null
        })) : []
    };

    return transformedStep;
};

export const quizService = {
    async getSelectedFunnel(funnelId: string) {
        const funnelVersion = funnelId.replace('f', '')
        const url = `${SERVER_URL}/api/funnels?filters[version]=${funnelVersion}&populate[1]=steps&populate[2]=entryPoint&populate[3]=stepProgress&populate[4]=stepProgress.funnelStep`;
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to fetch funnel steps: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        if (!data || !data.data) {
            throw new Error('Invalid response format: missing data');
        }
        return data.data[0];
    },
    async getFunnelSteps(funnelId: string): Promise<FunnelStep[]> {
        try {
            // First, get available locales from Strapi
            const localesResponse = await fetch(`${SERVER_URL}/api/i18n/locales`);
            if (!localesResponse.ok) {
                throw new Error(`Failed to fetch available locales: ${localesResponse.status} ${localesResponse.statusText}`);
            }

            const locales: Locale[] = await localesResponse.json();
            if (!Array.isArray(locales)) {
                throw new Error('Invalid locales response format');
            }
            const currentLocales = LOCALES.split(',')
            const filteredLocales = locales.filter(item => currentLocales.includes(item.code))

            // Find the appropriate locale to use
            let selectedLocale = getCurrentI18nLocale(filteredLocales);

            // Fetch funnel steps for the given funnelId with the selected locale
            const url = `${SERVER_URL}/api/funnel-steps?filters[funnel][id][$eq]=${funnelId}&locale=${selectedLocale}`;

            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch funnel steps: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            if (!data || !data.data) {
                throw new Error('Invalid response format: missing data');
            }

            return data.data.map((step: any) => ({
                id: step.id,
                documentId: step.documentId,
                stepIndex: step.stepIndex,
                totalSteps: step.totalSteps,
                stepType: step.stepType,
                title: step.title,
                subtitle: step.subtitle
            }));
        } catch (error) {
            console.error('Error fetching funnel steps:', error);
            throw error;
        }
    },


    async getFunnelStepDetails(documentId: string, stepType: string): Promise<FunnelStep | null> {
        try {
            const localesResponse = await fetch(`${SERVER_URL}/api/i18n/locales`);
            if (!localesResponse.ok) {
                throw new Error(`Failed to fetch available locales: ${localesResponse.status} ${localesResponse.statusText}`);
            }

            const locales: Locale[] = await localesResponse.json();
            if (!Array.isArray(locales)) {
                throw new Error('Invalid locales response format');
            }

            const currentLocales = LOCALES.split(',')
            const filteredLocales = locales.filter(item => currentLocales.includes(item.code))


            // Find the appropriate locale to use
            let selectedLocale = getCurrentI18nLocale(filteredLocales);

            // Different population parameters based on step type
            let populateParams = '';
            if (stepType === 'INFO') {
                populateParams = `populate[0]=stepContent&populate[1]=stepContent.options&populate[2]=stepContent.mainBanner&locale=${selectedLocale}`;
            } else if (stepType === 'EXTRA_INFO') {
                populateParams = `populate[0]=stepContent&populate[1]=stepContent.infoCards&populate[2]=stepContent.mainBanner&populate[3]=stepContent.infoCards.icon&locale=${selectedLocale}`;
            } else if (stepType === 'PROCESSING') {
                populateParams = `populate[0]=stepContent&populate[1]=stepContent.testimonials&populate[2]=stepContent.testimonials.avatar&locale=${selectedLocale}`;
            } else if (stepType === 'RESULT') {
                populateParams = `populate[0]=stepContent&populate[1]=stepContent.attributes&populate[2]=stepContent.attributes.icon&locale=${selectedLocale}`;
            } else if (stepType === 'SUBSCRIPTION_DETAIL') {
                populateParams = `populate[0]=stepContent&populate[1]=stepContent.comparisonCards&populate[2]=stepContent.comments&populate[3]=stepContent.featureList&populate[4]=stepContent.supportBlock&populate[5]=stepContent.futureSkills&populate[6]=stepContent.frequentQuestions&populate[7]=stepContent.futureSkills.items&populate[8]=stepContent.comparisonCards.image&populate[9]=stepContent.comments.avatar&populate[10]=stepContent.featureList.items&populate[11]=stepContent.frequentQuestions.items&populate[12]=stepContent.featureList.items.icon&populate[13]=stepContent.supportBlock.banner&populate[14]=stepContent.futureSkills.items.icon&locale=${selectedLocale}`;
            } else {
                populateParams = `populate[0]=stepContent&populate[1]=stepContent.options&populate[2]=stepContent.options.image&populate[3]=stepContent.options.emoji&locale=${selectedLocale}`;
            }

            const url = `${SERVER_URL}/api/funnel-steps?filters[documentId][$eq]=${documentId}&${populateParams}`;
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error('Failed to fetch funnel step details');
            }
            const data = await response.json();
            if (!data || !data.data || !data.data[0]) {
                return null;
            }
            return transformStep(data.data[0]);
        } catch (error) {
            console.error('Error fetching funnel step details:', error);
            throw error;
        }
    },

    async getProductPrices(funnelIndex: string): Promise<any> {
        try {
            const url = `${SERVER_URL}/api/prices/${funnelIndex}`;

            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch product prices: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching product prices:', error);
            throw error;
        }
    },
    async createSingleSub(email: string, priceId: string, paymentId: string, name: string, couponId: string, productId: string, secondPriceId: string): Promise<any> {
        try {
            const url = `${SERVER_URL}/api/create-single-sub`;

            const res = await fetch(url, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    email,
                    trialPriceId:priceId,
                    recurringPriceId: secondPriceId,
                    paymentMethodId: paymentId,
                    name,
                    couponId,
                    productId
                }),
            });

            const data = await res.json();
            return data;
        } catch (error) {
            console.error('Error fetching product prices:', error);
            throw error;
        }
    },
    async createSub(email: string, priceId: string, paymentId: string, name: string, couponId: string, productId: string): Promise<any> {
        try {
            const url = `${SERVER_URL}/api/create-subscription`;

            const res = await fetch(url, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    email,
                    priceId,
                    paymentMethodId: paymentId,
                    name,
                    couponId,
                    productId
                }),
            });

            const data = await res.json();
            return data;
        } catch (error) {
            console.error('Error fetching product prices:', error);
            throw error;
        }
    },
    async savePurchaseInfo({user_id, order_id, amount, currency, product_id, purchase_kind}: {
        user_id: string
        order_id: string
        amount: number
        currency: string
        product_id: string
        purchase_kind: string
    }) {
        const user_id_storage = getOrCreateAttributionUserId();
        try {
            const url = `${SERVER_URL}/api/user-data/save-purchase`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    user_id: user_id_storage,
                    order_id, amount, currency, product_id, purchase_kind
                })
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch coupon: ${response.status} ${response.statusText}`);
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching coupon:', error);
            throw error;
        }
    },
    async getCouponByProductId(productId: string): Promise<any> {
        try {
            const url = `${SERVER_URL}/api/coupons/${productId}`;
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch coupon: ${response.status} ${response.statusText}`);
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching coupon:', error);
            throw error;
        }
    },

    async saveFunnelData(funnelData: any): Promise<any> {
        const user_id = getOrCreateAttributionUserId();
        try {
            const url = `${SERVER_URL}/api/user-data/save`;
            const created_at = new Date().toISOString()

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ...funnelData,
                    created_at,
                    user_id,
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to save funnel data: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error saving funnel data:', error);
            throw error;
        }
    },

    async saveAttributionTouch(payload: any): Promise<any> {
        try {
            const url = `${SERVER_URL}/api/user-data/save-touch`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            if (!response.ok) {
                throw new Error(`Failed to save attribution touch: ${response.status} ${response.statusText}`);
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error saving attribution touch:', error);
            // Do not rethrow to avoid breaking user navigation; return null for now
            return null;
        }
    },

    async saveUserEmail(email: string, news_letter_status: boolean) {
        const user_id = getOrCreateAttributionUserId();
        try {
            const url = `${SERVER_URL}/api/user-data/save-email`;

            const created_at = new Date().toISOString()
            console.log('save email data ', {email, user_id, created_at, news_letter_status})

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({email, user_id, created_at, news_letter_status})
            });

            if (!response.ok) {
                throw new Error(`Failed to save funnel data: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error saving funnel data:', error);
            throw error;
        }
    },

    async registerUser(username: string, email: string, password: string): Promise<any> {
        console.log("Register user ")
        try {
            const url = `${SERVER_URL}/api/auth/local/register`;
            const res = await fetch(url, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({username, email, password}),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error?.message || 'Registration failed');
            return data;
        } catch (error) {
            console.error('Error registering user:', error);
            throw error;
        }
    },
    async loginUser(identifier: string, password: string): Promise<any> {
        try {
            const url = `${SERVER_URL}/api/auth/local`;
            const res = await fetch(url, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({identifier, password}),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error?.message || 'Login failed');
            return data;
        } catch (error) {
            console.error('Error logging in user:', error);
            throw error;
        }
    },
    async getFunnels(): Promise<any[]> {
        try {
            const response = await fetch(`${SERVER_URL}/api/funnels`);
            if (!response.ok) {
                throw new Error(`Failed to fetch funnels: ${response.status} ${response.statusText}`);
            }
            const data = await response.json();
            if (!data || !data.data) {
                throw new Error('Invalid response format: missing data');
            }
            // Map to a simple array of funnel objects
            return data.data.map((funnel: any) => ({
                id: funnel.id,
                name: funnel.name || funnel.title || '',
                version: funnel.version || '1',
                ...funnel
            }));
        } catch (error) {
            console.error('Error fetching funnels:', error);
            throw error;
        }
    },
    async sendSupportEmail(supportData: SupportEmailData) {
        try {
            const url = `${SERVER_URL}/api/product-user/support-mail`;
            const res = await fetch(url, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({...supportData}),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error?.message || 'Send feedback failed!');
            return data;
        } catch (error) {
            console.error('Error sending feedback:', error);
            throw error;
        }
    }
}
